import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { mediaQueries } from '@ecs-helpers';
import { SearchCriteria } from '@ecs-services/search/search-help/search-criteria';
import { SearchCriteriaExplanationService } from '@ecs-services/search/search-help/search-criteria-explanation.service';
import { SearchService } from '@ecs-services/search/search.service';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { OnDestroyBase } from '@os-components';
import {
    SewButton,
    SewHeadline,
    SewMarkdown,
    SewRadio,
    SewRadioGroup,
    SewSearchField,
    SewTooltip,
    SewWsRichtext,
} from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    SparePart,
    SpareParts,
} from '../../../../../features/result/spare-parts-result/spare-parts.interface';
import { ReplaceDoubleLinebreakPipe } from '../../../../../pipes/replace-linebreak.pipe';
import { BasketItem, BasketItemType, ECoverBasketItem } from '../../../../../services/basket.service';
import { isSerialNumberResult } from '../../../../../services/search/search.helper';
import { PartAmounts } from '../../../../part-table/part-table.component';
import { InjectionDataStep, StepType, WIZARD_STEP_TOKEN } from '../../part-configuration-wizard.component';
import { StoreStepItems } from '../../part-configuration-wizard.service';
export interface ECoverConfigurationData {
    type: StepType.E_COVER;
    eCoverParts: SpareParts;
    partAmounts: PartAmounts;
    serialNumber?: string;
    isDtcKit?: boolean;
}

export type ECover = SparePart & {
    isPainted: boolean;
    isDtcKit: boolean;
    eCoverSerialNumber?: string;
    error?: boolean;
};
@Component({
    selector: 'ecs-e-cover-configuration',
    templateUrl: './e-cover-configuration.component.html',
    styleUrls: ['./e-cover-configuration.component.scss'],
    imports: [
        TranslateDirective,
        AsyncPipe,
        TranslatePipe,
        ReplaceDoubleLinebreakPipe,
        SewHeadline,
        SewRadioGroup,
        SewRadio,
        SewSearchField,
        SewButton,
        SewTooltip,
        SewMarkdown,
        SewWsRichtext,
        CommonModule,
    ],
})
export class ECoverConfigurationComponent extends OnDestroyBase implements OnInit, OnDestroy {
    public eCoverParts!: ECover[];
    public serialNumber?: string;

    public serialNumberCriteriaExplanation?: Observable<SearchCriteria>;
    public isMobile = false;

    private isDtcKit?: boolean;
    private stepItems: StoreStepItems = {};
    private partAmounts: PartAmounts;

    public constructor(
        @Inject(WIZARD_STEP_TOKEN)
        private injection: InjectionDataStep<ECoverConfigurationData>,
        private searchService: SearchService,
        private searchCriteriaExplanationService: SearchCriteriaExplanationService,
        private breakpointObserver: BreakpointObserver
    ) {
        super();
        this.restoreSavedData();
        this.serialNumber = this.injection.stepData.serialNumber;
        this.partAmounts = this.injection.stepData.partAmounts;
        this.isDtcKit = this.injection.stepData.isDtcKit;
        if (this.serialNumber && this.serialNumber.length) {
            this.injection.valid.next(true);
        }
    }

    public ngOnInit() {
        this.serialNumberCriteriaExplanation =
            this.searchCriteriaExplanationService.serialNumberSearchCriteria;

        this.breakpointObserver
            .observe(mediaQueries.mq3)
            .pipe(takeUntil(this.destroySubject))
            .subscribe(({ matches }) => {
                this.isMobile = !matches;
            });

        this.injection.nextStep.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            this.eCoverParts.forEach((eCover) => {
                if (
                    this.addForSerialNumberSearchResult(this.serialNumber) &&
                    this.isDtcKit !== undefined &&
                    eCover.installedQuantity
                ) {
                    this.stepItems[eCover.id] = [
                        {
                            basketItem: {
                                type: BasketItemType.E_COVER,
                                productId: eCover.id,
                                isDtcKit: this.isDtcKit,
                                isPainted: eCover.isPainted,
                                serialNumber: this.serialNumber,
                                quantity: this.partAmounts[(eCover as any).uniqueId],
                            },
                        },
                    ];
                } else {
                    this.stepItems[eCover.id] = [
                        {
                            basketItem: {
                                type: BasketItemType.E_COVER,
                                productId: eCover.id,
                                isDtcKit: eCover.isDtcKit,
                                isPainted: eCover.isPainted,
                                serialNumber: eCover.eCoverSerialNumber ?? '',
                                quantity: this.partAmounts[(eCover as any).uniqueId],
                            },
                        },
                    ];
                }
            });
            this.injection.addBasketItems(this.stepItems);
        });
    }

    public onSearch(eCover: ECover, searchTerm: string) {
        this.searchService
            .getSearchResult([searchTerm])
            .pipe(takeUntil(this.destroySubject))
            .subscribe({
                next: (result) => {
                    if (isSerialNumberResult(result.result)) {
                        eCover.error = false;
                        eCover.isDtcKit = result.result.isDtcKit;
                        eCover.eCoverSerialNumber = searchTerm;

                        if (this.eCoverParts.every((entry) => entry.error === false)) {
                            this.injection.valid.next(true);
                        }
                    } else {
                        eCover.error = true;
                    }
                },
                // valid serialnumber pattern but no data found on backend
                error: () => {
                    eCover.error = true;
                },
            });
    }

    private restoreSavedData() {
        this.eCoverParts = this.injection.stepData.eCoverParts.map((part) => {
            const storedPart = this.injection.storedData
                ? this.injection.storedData[part.id]
                    ? // alawys part at index 0, because there is only one part for e covers
                      this.injection.storedData[part.id][0]
                    : undefined
                : undefined;

            return storedPart && this.isECoverBasketItem(storedPart.basketItem)
                ? {
                      ...part,
                      isPainted: storedPart.basketItem.isPainted,
                      isDtcKit: storedPart.basketItem.isDtcKit,
                      eCoverSerialNumber: storedPart.basketItem.serialNumber,
                  }
                : // default values if not already stored
                  { ...part, isPainted: true, isDtcKit: false };
        });
    }

    private isECoverBasketItem(basketItem: BasketItem): basketItem is ECoverBasketItem {
        return (
            basketItem &&
            basketItem.type === BasketItemType.E_COVER &&
            typeof basketItem.serialNumber === 'string' &&
            basketItem.serialNumber !== undefined
        );
    }

    private addForSerialNumberSearchResult(serialNumber: string | undefined): serialNumber is string {
        return serialNumber !== undefined;
    }
}
